
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Car } from '../types';
import { dataService } from '../services/dataService';
import { CarCard } from '../components/CarCard';
import { ArrowRight, CheckCircle, Award, ShieldCheck, Car as CarIcon, Search } from 'lucide-react';
import { Logo } from '../components/Logo';

export const Home: React.FC = () => {
  const [featuredCars, setFeaturedCars] = useState<Car[]>([]);

  useEffect(() => {
    const fetchCars = async () => {
      const allCars = await dataService.getCars();
      // Increased slice to 8 vehicles minimum
      setFeaturedCars(allCars.filter(c => c.featured).slice(0, 8));
    };
    fetchCars();
  }, []);

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <div className="relative h-[700px] w-full overflow-hidden bg-brandDark">
        <div 
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1494905998402-395d579af36f?auto=format&fit=crop&q=80&w=2000")' }}
        ></div>
        <div className="absolute inset-0 bg-brandDark/50 backdrop-blur-[2px]"></div>
        
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center items-center text-center">
          <div className="max-w-4xl animate-fade-in-up">
            {/* Logo Badge for Hero */}
            <div className="bg-white/95 p-4 rounded-2xl shadow-2xl mb-10 inline-block border border-white/20">
               <Logo size="lg" variant="dark" />
            </div>
            
            <h2 className="text-accent font-heading text-xl md:text-2xl font-bold uppercase tracking-[0.3em] mb-4 drop-shadow-lg">
              Chicago's Premium Dealership
            </h2>
            <h1 className="text-6xl md:text-9xl font-heading font-bold text-white leading-none mb-10 uppercase drop-shadow-2xl">
              DRIVE <span className="text-primary">SMARTER</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-3xl mx-auto font-light leading-relaxed">
              Experience the standard in pre-owned automotive excellence. Hand-selected inventory. Competitive rates. Absolute integrity.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link 
                to="/inventory" 
                className="group inline-flex items-center justify-center px-12 py-5 bg-primary text-white font-heading text-xl font-bold uppercase tracking-widest hover:bg-white hover:text-primary transition-all duration-300 border-b-4 border-green-900 shadow-xl"
              >
                Browse Collection <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" />
              </Link>
              <Link 
                to="/request" 
                className="inline-flex items-center justify-center px-12 py-5 bg-white/10 backdrop-blur-md text-white border border-white/30 font-heading text-xl font-bold uppercase tracking-widest hover:bg-white hover:text-brandDark transition-all duration-300 shadow-xl"
              >
                Custom Search
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Search Bar */}
      <div className="bg-white py-10 border-b border-gray-100 shadow-sm relative z-10 -mt-10 mx-4 lg:mx-auto max-w-6xl rounded-xl">
        <div className="px-6 md:px-12">
          <div className="flex flex-wrap items-center justify-center lg:justify-between gap-8">
            <div className="flex items-center gap-6 text-brandDark">
              <Search size={40} className="text-primary" />
              <div>
                <h3 className="font-heading text-xl font-bold uppercase leading-tight tracking-wide">Ready to find yours?</h3>
                <p className="text-[11px] text-gray-400 uppercase font-bold tracking-widest">Explore popular categories</p>
              </div>
            </div>
            
            <div className="flex flex-wrap justify-center gap-10">
              <Link to="/inventory" className="group flex flex-col items-center gap-2">
                <div className="bg-brandGray p-3 rounded-xl group-hover:bg-primary group-hover:text-white transition-all">
                  <CarIcon size={24} />
                </div>
                <span className="font-heading text-xs font-bold uppercase tracking-widest">Sedans</span>
              </Link>
              <Link to="/inventory" className="group flex flex-col items-center gap-2">
                <div className="bg-brandGray p-3 rounded-xl group-hover:bg-accent group-hover:text-brandDark transition-all">
                  <CarIcon size={24} />
                </div>
                <span className="font-heading text-xs font-bold uppercase tracking-widest">SUVs</span>
              </Link>
              <Link to="/inventory" className="group flex flex-col items-center gap-2">
                <div className="bg-brandGray p-3 rounded-xl group-hover:bg-danger group-hover:text-white transition-all">
                  <CarIcon size={24} />
                </div>
                <span className="font-heading text-xs font-bold uppercase tracking-widest">Trucks</span>
              </Link>
              <Link to="/inventory" className="group flex flex-col items-center gap-2">
                <div className="bg-brandGray p-3 rounded-xl group-hover:bg-brandDark group-hover:text-white transition-all">
                  <CarIcon size={24} />
                </div>
                <span className="font-heading text-xs font-bold uppercase tracking-widest">Sport</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Vehicles */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-end mb-16">
            <div className="text-left">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-1.5 w-10 bg-primary"></div>
                <span className="text-xs font-bold uppercase tracking-[0.3em] text-primary">In Stock Now</span>
              </div>
              <h2 className="font-heading text-5xl font-bold text-brandDark uppercase leading-tight">Hand-Selected <br /> <span className="text-primary">Inventory</span></h2>
            </div>
            <Link 
              to="/inventory" 
              className="mt-6 md:mt-0 inline-flex items-center gap-3 font-heading text-sm font-bold uppercase border-2 border-brandDark px-10 py-4 hover:bg-brandDark hover:text-white transition-all tracking-widest"
            >
              All Arrivals <ArrowRight size={20} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredCars.length > 0 ? (
              featuredCars.map(car => <CarCard key={car.id} car={car} />)
            ) : (
              <div className="col-span-4 text-center text-gray-500 py-10 font-bold uppercase tracking-widest animate-pulse">Scanning Our Lot...</div>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-brandGray border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
                <img 
                  src="https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&q=80&w=1000" 
                  alt="Modern Showroom" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl -z-0"></div>
              <div className="absolute -bottom-10 -right-10 w-60 h-60 bg-accent/20 rounded-full blur-3xl -z-0"></div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="bg-white p-4 rounded-xl shadow-sm mb-10 w-fit border border-gray-100">
                <Logo size="md" variant="dark" />
              </div>
              <h2 className="font-heading text-5xl font-bold uppercase mb-10 text-brandDark leading-tight">
                The <span className="text-primary">Dakar</span> Automotive Standard
              </h2>
              <div className="space-y-10">
                <div className="flex gap-8 group">
                  <div className="flex-shrink-0 h-16 w-16 bg-white shadow-lg border-b-4 border-primary flex items-center justify-center rounded-2xl group-hover:scale-110 transition-transform duration-300">
                    <ShieldCheck size={32} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold uppercase mb-3">Mechanical Mastery</h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-md">Our certified master technicians perform a 160-point inspection on every vehicle before it earns the Dakar seal of approval.</p>
                  </div>
                </div>
                <div className="flex gap-8 group">
                  <div className="flex-shrink-0 h-16 w-16 bg-white shadow-lg border-b-4 border-accent flex items-center justify-center rounded-2xl group-hover:scale-110 transition-transform duration-300">
                    <Award size={32} className="text-brandDark" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold uppercase mb-3">Seamless Finance</h3>
                    <p className="text-gray-500 text-sm leading-relaxed max-w-md">Access our nationwide lender network to secure prime rates tailored to your financial goals, with same-day approvals.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
